import { useState, useEffect } from "react";
import Button from "../../../components/base/Button";
import { postHeroData, getHeroData, generateContent } from "../../../api/apiServices";

export default function HeroEditor() {
  const [heroData, setHeroData] = useState({
    statusBadge: "",
    mainTitle: "",
    subTitle: "",
    description: "",
    primaryButtonText: "",
    secondaryButtonText: "",
    backgroundImage: "",
    features: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // 🟢 Load saved hero section when editor opens
  useEffect(() => {
    const loadHeroData = async () => {
      try {
        setLoading(true);
        const data = await getHeroData();

        if (data) {
          setHeroData({
            statusBadge: data.statusBadge || "",
            mainTitle: data.mainTitle || "",
            subTitle: data.subTitle || "",
            description: data.description || "",
            primaryButtonText: data.primaryButtonText || "",
            secondaryButtonText: data.secondaryButtonText || "",
            backgroundImage: data.backgroundImage || "",
            features: data.features?.length
              ? data.features
              : [
                  {
                    icon: "ri-star-line",
                    title: "New Feature",
                    description: "Feature description",
                    gradient: "from-blue-500 to-cyan-600",
                  },
                ],
          });
        }
      } catch (error) {
        console.error("Failed to fetch hero data:", error);
        setMessage("⚠️ Could not load existing hero data");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    loadHeroData();
  }, []);

  // === Input handlers ===
  const handleInputChange = (field, value) => {
    setHeroData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setHeroData((prev) => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? { ...feature, [field]: value } : feature
      ),
    }));
  };

  const addFeature = () => {
    setHeroData((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        {
          icon: "ri-star-line",
          title: "New Feature",
          description: "Feature description",
          gradient: "from-blue-500 to-cyan-600",
        },
      ],
    }));
  };

  const removeFeature = (index) => {
    setHeroData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // === 🪄 Generate Background Image + Auto Update Prompt ===
const handleGenerateImage = async (prompt) => {
  if (!prompt.trim()) {
    setMessage("⚠️ Please enter a background prompt first.");
    return;
  }

  try {
    setLoading(true);
    setMessage("🎨 Generating background image and content...");

    const response = await generateContent({ prompt });

    if (response) {
      setHeroData((prev) => ({
        ...prev,
        backgroundImage: response.image || prev.backgroundImage,
        // If AI returned a long description, auto-update prompt text with it
        backgroundPrompt:
          response.longDescription || prev.backgroundPrompt || prompt,
      }));

      setMessage("✅ Background image and description generated successfully!");
    } else {
      setMessage("⚠️ No content returned from the generator.");
    }
  } catch (error) {
    console.error("Image generation failed:", error);
    setMessage("❌ Failed to generate image or description.");
  } finally {
    setLoading(false);
  }
};


  // === Submit handler ===
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setMessage(null);
      await postHeroData(heroData);
      setMessage("✅ Hero data saved successfully!");
    } catch (error) {
      console.error(error);
      setMessage("❌ Failed to save hero data.");
    } finally {
      setLoading(false);
    }
  };

  // === Loader State ===
  if (initialLoad) {
    return (
      <div className="flex justify-center items-center h-64 text-slate-600">
        <i className="ri-loader-4-line animate-spin text-3xl mr-2"></i>
        Loading hero section...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <i className="ri-home-line mr-3 text-emerald-600"></i>
          Hero Section Settings
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* === Left Column === */}
        <div className="space-y-6">
          <div>
            <label className="block text-slate-700 font-semibold mb-3">
              Status Badge
            </label>
            <input
              type="text"
              value={heroData.statusBadge}
              onChange={(e) => handleInputChange("statusBadge", e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
              placeholder="Available for New Projects"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-3">
                Main Title
              </label>
              <input
                type="text"
                value={heroData.mainTitle}
                onChange={(e) => handleInputChange("mainTitle", e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Craft Digital"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-3">
                Sub Title
              </label>
              <input
                type="text"
                value={heroData.subTitle}
                onChange={(e) => handleInputChange("subTitle", e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Experiences"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-3">
              Description
            </label>
            <textarea
              value={heroData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
              placeholder="Your compelling description..."
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-3">
                Primary Button Text
              </label>
              <input
                type="text"
                value={heroData.primaryButtonText}
                onChange={(e) =>
                  handleInputChange("primaryButtonText", e.target.value)
                }
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Start Your Project"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-3">
                Secondary Button Text
              </label>
              <input
                type="text"
                value={heroData.secondaryButtonText}
                onChange={(e) =>
                  handleInputChange("secondaryButtonText", e.target.value)
                }
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="View Portfolio"
              />
            </div>
          </div>

          {/* === Background Image Generator === */}
          <div>
  <label className="block text-slate-700 font-semibold mb-3">
    Background Image Prompt
  </label>

  <div className="relative">
    <textarea
      value={heroData.backgroundPrompt || heroData.backgroundImage || ""}
      onChange={(e) =>
        handleInputChange("backgroundPrompt", e.target.value)
      }
      rows={3}
      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
      placeholder="Describe or refine your background image prompt..."
    />

    {/* Generate Button */}
    <button
      type="button"
      onClick={() => handleGenerateImage(heroData.backgroundPrompt)}
      disabled={loading}
      className="absolute bottom-3 right-3 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
    >
      {loading ? (
        <>
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Generating...
        </>
      ) : (
        <>
          <i className="ri-magic-line mr-2"></i> Generate
        </>
      )}
    </button>
  </div>

  {/* 🖼️ Preview generated image */}
  {heroData.backgroundImage?.startsWith("http") && (
    <div className="mt-4">
      <img
        src={heroData.backgroundImage}
        alt="Generated Background"
        className="w-full rounded-xl border border-slate-200 shadow-sm"
      />
      <p className="text-sm text-slate-500 mt-2 text-center">
        Preview of generated image
      </p>
    </div>
  )}

  <p className="text-sm text-slate-500 mt-2">
    The prompt will update automatically if AI returns a refined description.
  </p>
</div>


        </div>

        {/* === Right Column === */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-800">Feature Cards</h3>
            <Button
              onClick={addFeature}
              size="sm"
              variant="outline"
              className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
            >
              <i className="ri-add-line mr-2"></i>
              Add Feature
            </Button>
          </div>

          <div className="space-y-6">
            {heroData.features.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-800">
                    Feature {index + 1}
                  </h4>
                  {heroData.features.length > 1 && (
                    <button
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-300"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-600 font-medium mb-2">
                        Icon (Remix Icon)
                      </label>
                      <input
                        type="text"
                        value={feature.icon}
                        onChange={(e) =>
                          handleFeatureChange(index, "icon", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                        placeholder="ri-star-line"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-medium mb-2">
                        Gradient Colors
                      </label>
                      <input
                        type="text"
                        value={feature.gradient}
                        onChange={(e) =>
                          handleFeatureChange(index, "gradient", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                        placeholder="from-blue-500 to-cyan-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) =>
                        handleFeatureChange(index, "title", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                      placeholder="Feature title"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-2">
                      Description
                    </label>
                    <textarea
                      value={feature.description}
                      onChange={(e) =>
                        handleFeatureChange(index, "description", e.target.value)
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                      placeholder="Feature description"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === Floating Save Button Bar === */}
<div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-4 flex items-center justify-between z-50">
  <p className="text-slate-600 font-medium">
    {message || "All changes are saved locally until you click Save."}
  </p>
  <Button
    onClick={handleSubmit}
    disabled={loading}
    className="bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-2 rounded-lg shadow-md"
  >
    {loading ? (
      <>
        <i className="ri-loader-4-line animate-spin mr-2"></i> Saving...
      </>
    ) : (
      <>
        <i className="ri-save-3-line mr-2"></i> Save Hero Section
      </>
    )}
  </Button>
</div>

    </div>
  );
}
